import { Component, computed, inject, signal } from '@angular/core';
import { PROFILE_PAGE_DATA_TOKEN } from '../../tokens-and-interfaces';
import { CrmFieldService, CrmObjectDisplayService, StandardIds } from '../../shared-services';
import { CrmObjectInterface } from '@vendasta/crm';
import { BadgeColor, GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { TranslateModule } from '@ngx-translate/core';
import { UpdaterService } from '../model-driven-form';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter } from 'rxjs';

@Component({
  selector: 'crm-opportunity-status-badge',
  templateUrl: './opportunity-status-badge.component.html',
  styleUrls: ['./opportunity-status-badge.component.scss'],
  imports: [GalaxyBadgeModule, TranslateModule],
})
export class CrmOpportunityStatusBadgeComponent {
  private readonly data = inject(PROFILE_PAGE_DATA_TOKEN);
  private readonly fieldService = inject(CrmFieldService);
  private readonly objectDisplayService = inject(CrmObjectDisplayService);
  private readonly updaterService = inject(UpdaterService);

  private readonly statusValue = signal<string>(this.getStatusFieldValue(this.data.crmObject));
  protected readonly title = signal<string>(this.getTitle(this.data.crmObject));
  protected readonly status = computed<string>(() => this.getStatus(this.statusValue()));
  protected readonly statusColor = computed<BadgeColor>(() => this.getStatusColor(this.status()));

  constructor() {
    this.updaterService.updatedField
      .pipe(
        takeUntilDestroyed(),
        filter((field) => field.fieldId === StandardIds.OpportunityStatus),
        debounceTime(100),
      )
      .subscribe((field) => this.statusValue.set(field.stringValue ?? 'Open'));
  }

  private getTitle(crmObject?: CrmObjectInterface): string {
    if (crmObject) {
      return this.objectDisplayService.getObjectDisplayName('Opportunity', crmObject);
    }
    return '';
  }

  private getStatusFieldValue(crmObject?: CrmObjectInterface): string {
    return crmObject
      ? this.fieldService.getFieldValueFromCrmObject(crmObject, StandardIds.OpportunityStatus)?.stringValue || ''
      : '';
  }

  private getStatus(status?: string): string {
    switch (status) {
      case 'Closed Won':
        return 'OPPORTUNITY.STATUS.WON';
      case 'Closed Lost':
        return 'OPPORTUNITY.STATUS.LOST';
    }
    return '';
  }

  private getStatusColor(i18nStatusKey: string): BadgeColor {
    switch (i18nStatusKey) {
      case 'OPPORTUNITY.STATUS.WON':
        return 'green-solid';
      case 'OPPORTUNITY.STATUS.LOST':
        return 'red-solid';
    }
    return 'blue';
  }
}
